import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { useConfirm } from '../../components/ConfirmModal';

const PLAN_COLOR = { free: '#6b7280', basic: '#6f42c1', family: '#059669' };
const PLAN_BG    = { free: 'var(--header-bg)', basic: 'var(--purple-section)', family: 'var(--green-section)' };

const InfoRow = ({ label, children }) => (
  <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid var(--row-sep)' }}>
    <span style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{label}</span>
    <span style={{ fontSize: '13px', color: 'var(--text-main)', fontWeight: 500 }}>{children}</span>
  </div>
);

const AdminUserDetail = () => {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [error, setError]     = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendInput, setShowSuspendInput] = useState(false);

  // Credit adjust form
  const [credits, setCredits] = useState({ storyCredits: '', narrationCredits: '', cloneCredits: '', regenCredits: '', photoCredits: '', resetUsage: false });
  const [creditMode, setCreditMode] = useState('adjust'); // 'adjust' | 'set'

  const load = useCallback(() => {
    setLoading(true);
    API.get(`/admin/users/${id}`)
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load user.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patch = async (update) => {
    setSaving(true); setMsg(''); setError('');
    try {
      await API.patch(`/admin/users/${id}`, update);
      setMsg('Saved.');
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Error saving.');
    } finally { setSaving(false); }
  };

  const handleCredits = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setError('');
    try {
      await API.post(`/admin/users/${id}/credits`, { ...credits, mode: creditMode });
      setMsg('Credits updated.');
      setCredits({ storyCredits: '', narrationCredits: '', cloneCredits: '', regenCredits: '', resetUsage: false });
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Error.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete user permanently?', message: 'This will delete the user and ALL their data — stories, children, subscriptions. This cannot be undone.', confirmLabel: 'Delete Everything', danger: true });
    if (!ok) return;
    try {
      await API.delete(`/admin/users/${id}`);
      navigate('/admin/users');
    } catch (e) {
      setError('Failed to delete user.');
    }
  };

  if (loading) return (
    <AdminLayout title="User Detail">
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
      </div>
    </AdminLayout>
  );

  if (error && !data) return (
    <AdminLayout title="User Detail">
      <div className="alert alert-danger">{error}</div>
      <Link to="/admin/users" className="btn btn-outline-secondary rounded-pill">← Back to Users</Link>
    </AdminLayout>
  );

  const { user, children, recentStories, storyCount, voiceProfile, limits, stripeInvoices, giftOrders } = data;

  return (
    <AdminLayout title={user.name}>
      <div className="d-flex align-items-center gap-3 mb-4">
        <Link to="/admin/users" className="btn btn-sm btn-outline-secondary rounded-pill">
          <i className="bi bi-arrow-left me-1"></i>Back
        </Link>
        <h5 className="mb-0 fw-bold" style={{ color: 'var(--text-main)' }}>{user.name}</h5>
        {user.isAdmin && <span className="badge bg-danger">Admin</span>}
        {user.isSuspended && <span className="badge bg-warning text-dark">Suspended</span>}
      </div>

      {msg   && <div className="alert alert-success py-2 small">{msg}</div>}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="row g-3">
        {/* Left column */}
        <div className="col-lg-4">

          {/* Account info */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="text-center mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white mx-auto mb-2"
                  style={{ width: '60px', height: '60px', background: '#6f42c1', fontSize: '22px' }}>
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <div style={{ fontWeight: 700, color: 'var(--text-main)' }}>{user.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{user.email}</div>
                <a href={`mailto:${user.email}`} className="btn btn-sm btn-outline-secondary rounded-pill mt-2 px-3" style={{ fontSize: '12px' }}>
                  <i className="bi bi-envelope me-1"></i>Send Email
                </a>
              </div>
              <InfoRow label="Joined">{new Date(user.createdAt).toLocaleDateString()}</InfoRow>
              <InfoRow label="Email Verified">
                <span className={`badge bg-${user.isEmailVerified ? 'success' : 'danger'} bg-opacity-10 text-${user.isEmailVerified ? 'success' : 'danger'}`}>
                  {user.isEmailVerified ? 'Yes' : 'No'}
                </span>
              </InfoRow>
              <InfoRow label="Total Stories">{storyCount}</InfoRow>
              <InfoRow label="Children">{children.length}</InfoRow>
              <InfoRow label="Voice Profile">
                {voiceProfile ? <span className="badge bg-success bg-opacity-10 text-success">Ready</span> : <span className="text-muted">None</span>}
              </InfoRow>
            </div>
          </div>

          {/* Subscription */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Subscription</h6>
              <InfoRow label="Plan">
                <span className="badge rounded-pill px-2" style={{ background: PLAN_BG[user.subscription?.plan], color: PLAN_COLOR[user.subscription?.plan] }}>
                  {user.subscription?.plan || 'free'}
                </span>
              </InfoRow>
              <InfoRow label="Status">{user.subscription?.status || 'inactive'}</InfoRow>
              <InfoRow label="Billing">{user.subscription?.billingInterval === 'year' ? 'Annual' : 'Monthly'}</InfoRow>
              {user.subscription?.currentPeriodEnd && (
                <InfoRow label="Renews">{new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}</InfoRow>
              )}
              {user.subscription?.stripeCustomerId && (
                <InfoRow label="Stripe Customer">
                  <a href={`https://dashboard.stripe.com/customers/${user.subscription.stripeCustomerId}`} target="_blank" rel="noreferrer"
                    style={{ fontSize: '11px', color: '#6f42c1', fontFamily: 'monospace' }}>
                    {user.subscription.stripeCustomerId.slice(0, 18)}…
                  </a>
                </InfoRow>
              )}
              {user.subscription?.stripeSubscriptionId && (
                <InfoRow label="Stripe Sub">
                  <a href={`https://dashboard.stripe.com/subscriptions/${user.subscription.stripeSubscriptionId}`} target="_blank" rel="noreferrer"
                    style={{ fontSize: '11px', color: '#6f42c1', fontFamily: 'monospace' }}>
                    {user.subscription.stripeSubscriptionId.slice(0, 18)}…
                  </a>
                </InfoRow>
              )}
              {user.subscription?.cancelAtPeriodEnd && (
                <div className="alert alert-warning py-1 small mt-2 mb-0">Cancels at period end</div>
              )}

              <hr className="my-3" />
              <div className="mb-2" style={{ fontSize: '13px', color: 'var(--text-muted2)', fontWeight: 600 }}>Change Plan</div>
              <div className="d-flex gap-2 flex-wrap">
                {['free','basic','family'].map(p => (
                  <button key={p} disabled={saving || user.subscription?.plan === p}
                    onClick={() => patch({ plan: p, status: p === 'free' ? 'inactive' : 'active' })}
                    className="btn btn-sm rounded-pill"
                    style={{ background: user.subscription?.plan === p ? PLAN_BG[p] : 'var(--header-bg)', color: PLAN_COLOR[p], border: `1px solid ${PLAN_COLOR[p]}33`, fontSize: '12px' }}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Account actions */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Account Actions</h6>
              <div className="d-flex flex-column gap-2">

                {/* Suspend / Unsuspend */}
                {user.isSuspended ? (
                  <div>
                    {user.suspendReason && (
                      <div className="alert alert-warning py-2 mb-2 small">
                        <strong>Suspend reason:</strong> {user.suspendReason}
                      </div>
                    )}
                    <button disabled={saving} onClick={() => { patch({ isSuspended: false }); setShowSuspendInput(false); setSuspendReason(''); }}
                      className="btn btn-sm btn-success rounded-pill w-100" style={{ fontSize: '13px' }}>
                      ✓ Unsuspend Account
                    </button>
                  </div>
                ) : (
                  <div>
                    {!showSuspendInput ? (
                      <button disabled={saving} onClick={() => setShowSuspendInput(true)}
                        className="btn btn-sm btn-warning rounded-pill w-100" style={{ fontSize: '13px' }}>
                        ⚠ Suspend Account
                      </button>
                    ) : (
                      <div className="p-3 rounded-3" style={{ background: 'var(--orange-section)', border: '1px solid #fed7aa' }}>
                        <label className="form-label fw-semibold small mb-1" style={{ color: '#9a3412' }}>Reason (shown to user)</label>
                        <textarea className="form-control form-control-sm mb-2" rows={2}
                          placeholder="e.g. Violation of Terms of Service — section 4.2"
                          value={suspendReason}
                          onChange={e => setSuspendReason(e.target.value)} />
                        <div className="d-flex gap-2">
                          <button disabled={saving} onClick={() => { patch({ isSuspended: true, suspendReason }); setShowSuspendInput(false); }}
                            className="btn btn-sm btn-warning rounded-pill flex-grow-1" style={{ fontSize: '13px' }}>
                            Confirm Suspend
                          </button>
                          <button onClick={() => { setShowSuspendInput(false); setSuspendReason(''); }}
                            className="btn btn-sm btn-outline-secondary rounded-pill" style={{ fontSize: '13px' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <button disabled={saving} onClick={() => patch({ isEmailVerified: true })}
                  className="btn btn-sm btn-outline-secondary rounded-pill" style={{ fontSize: '13px' }}>
                  <i className="bi bi-check-circle me-1"></i>Mark Email Verified
                </button>
                <button disabled={saving} onClick={() => patch({ isAdmin: !user.isAdmin })}
                  className="btn btn-sm btn-outline-secondary rounded-pill" style={{ fontSize: '13px' }}>
                  {user.isAdmin ? 'Remove Admin' : 'Grant Admin Access'}
                </button>
                <hr className="my-1" />
                <button onClick={handleDelete}
                  className="btn btn-sm btn-outline-danger rounded-pill" style={{ fontSize: '13px' }}>
                  <i className="bi bi-trash me-1"></i>Delete User &amp; All Data
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="col-lg-8">

          {/* Usage + credits */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Monthly Usage &amp; Credits</h6>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>
                    Resets: {user.lastStoryReset ? new Date(new Date(user.lastStoryReset).setMonth(new Date(user.lastStoryReset).getMonth() + 1)).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>

              {/* Usage bars */}
              <div className="row g-3 mb-4">
                {[
                  { label: 'Stories',          icon: '📖', used: user.storiesGeneratedThisMonth,       limit: limits?.stories,             topUp: user.storyTopUpCredits,                color: '#6f42c1', track: '#e9e0ff' },
                  { label: 'Smart Narrations', icon: '🔊', used: user.narrationsThisMonth,              limit: limits?.narrations,          topUp: user.narrationTopUpCredits,            color: '#0d6efd', track: '#dbeafe' },
                  { label: 'Voice Narrations', icon: '🎙️', used: user.cloneNarrationsThisMonth,        limit: limits?.cloneNarrations,     topUp: user.cloneNarrationTopUpCredits,       color: '#ea580c', track: '#fed7aa' },
                  { label: 'Illus. Regens',    icon: '🎨', used: user.regenIllustrationsThisMonth,     limit: limits?.regens,              topUp: user.regenTopUpCredits,                color: '#059669', track: '#bbf7d0' },
                  { label: 'Photo Illus.',     icon: '📸', used: user.photoIllustrationsThisMonth || 0, limit: limits?.photoIllustrations,  topUp: user.photoIllustrationTopUpCredits,    color: '#db2777', track: '#fce7f3' },
                ].map(({ label, icon, used, limit, topUp, color, track }) => {
                  const totalLimit = (limit || 0) + (topUp || 0);
                  const pct        = totalLimit > 0 ? Math.min(100, Math.round((used / totalLimit) * 100)) : 0;
                  const remaining  = Math.max(0, totalLimit - used);
                  const barColor   = pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : color;
                  return (
                    <div key={label} className="col-sm-6">
                      <div className="p-3 rounded-3" style={{ background: 'var(--page-bg)', border: '1px solid #ede8ff' }}>
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <div className="d-flex align-items-center gap-1">
                            <span style={{ fontSize: '14px' }}>{icon}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-main)' }}>{label}</span>
                          </div>
                          <span style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{used} / {totalLimit}</span>
                        </div>
                        <div style={{ background: track, borderRadius: '4px', height: '6px', marginBottom: '6px' }}>
                          <div style={{ width: `${pct}%`, background: barColor, height: '6px', borderRadius: '4px', transition: 'width 0.4s' }} />
                        </div>
                        <div className="d-flex justify-content-between">
                          <span style={{ fontSize: '11px', color: remaining === 0 ? '#dc2626' : '#6b7280' }}>
                            {remaining} remaining
                          </span>
                          {(topUp || 0) > 0 && (
                            <span style={{ fontSize: '11px', color: '#059669', fontWeight: 600 }}>
                              +{topUp} top-up
                            </span>
                          )}
                        </div>
                        {(topUp || 0) > 0 && (
                          <div style={{ fontSize: '10px', color: 'var(--text-soft)', marginTop: '2px' }}>
                            {Math.max(0, (limit || 0) - used)} plan + {topUp} bonus
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Adjust credits */}
              <div style={{ borderTop: '1px solid var(--row-sep)', paddingTop: '16px' }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="fw-semibold" style={{ fontSize: '14px', color: 'var(--text-main)' }}>
                    Manage Top-up Credits
                  </div>
                  <div className="d-flex gap-1 p-1 rounded-3" style={{ background: 'var(--header-bg)' }}>
                    {[['adjust', '+/− Adjust'], ['set', '= Set']].map(([val, lbl]) => (
                      <button key={val} type="button"
                        className="btn btn-sm rounded-2 px-3 py-1"
                        style={{ fontSize: '12px', fontWeight: 600, background: creditMode === val ? '#6f42c1' : 'transparent', color: creditMode === val ? 'white' : '#6b7280', border: 'none' }}
                        onClick={() => setCreditMode(val)}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleCredits}>
                  <div className="row g-2 mb-3">
                    {[
                      { key: 'storyCredits',     label: 'Stories',          icon: '📖', current: user.storyTopUpCredits },
                      { key: 'narrationCredits', label: 'Smart Narrations', icon: '🔊', current: user.narrationTopUpCredits },
                      { key: 'cloneCredits',     label: 'Voice Narrations', icon: '🎙️', current: user.cloneNarrationTopUpCredits },
                      { key: 'regenCredits',     label: 'Illus. Regens',    icon: '🎨', current: user.regenTopUpCredits },
                      { key: 'photoCredits',     label: 'Photo Illus.',     icon: '📸', current: user.photoIllustrationTopUpCredits },
                    ].map(({ key, label, icon, current }) => (
                      <div key={key} className="col-sm-6 col-lg-3">
                        <label className="form-label small mb-1" style={{ color: 'var(--text-body)', fontSize: '12px' }}>
                          {icon} {label}
                          <span className="ms-1" style={{ color: 'var(--text-soft)', fontSize: '11px' }}>({current || 0} now)</span>
                        </label>
                        <input type="number" className="form-control form-control-sm" style={{ borderRadius: '8px' }}
                          value={credits[key]}
                          onChange={e => setCredits(c => ({ ...c, [key]: e.target.value }))}
                          placeholder={creditMode === 'set' ? 'Set to…' : '+/− delta'} />
                      </div>
                    ))}
                  </div>

                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="resetUsage"
                        checked={credits.resetUsage}
                        onChange={e => setCredits(c => ({ ...c, resetUsage: e.target.checked }))} />
                      <label className="form-check-label small" htmlFor="resetUsage" style={{ color: 'var(--text-body)' }}>
                        Reset all monthly usage to 0
                      </label>
                    </div>
                    <button type="submit" disabled={saving} className="btn btn-sm btn-dream rounded-pill px-4">
                      {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</> : 'Apply Changes'}
                    </button>
                  </div>

                  <div className="mt-2" style={{ fontSize: '11px', color: 'var(--text-soft)' }}>
                    {creditMode === 'adjust'
                      ? 'Enter positive numbers to add credits, negative to remove. Leave blank to skip.'
                      : 'Enter the exact new value for each credit type. Leave blank to skip.'}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Children */}
          {children.length > 0 && (
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Child Profiles ({children.length})</h6>
                <div className="row g-2">
                  {children.map(c => (
                    <div key={c._id} className="col-sm-6">
                      <div className="p-3 rounded-3 d-flex align-items-center gap-2" style={{ background: 'var(--page-bg)' }}>
                        <span style={{ fontSize: '24px' }}>{c.avatar}</span>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>{c.name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Age {c.age} · {c.interests?.join(', ') || 'No interests set'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Payment history */}
          <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Payment History</h6>
              {(!stripeInvoices || stripeInvoices.length === 0) ? (
                <p className="text-muted small mb-0">No payment records found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>DATE</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>AMOUNT</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>STATUS</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>DESCRIPTION</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>INVOICE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stripeInvoices.map(inv => (
                        <tr key={inv.id}>
                          <td style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{new Date(inv.date).toLocaleDateString()}</td>
                          <td style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)' }}>
                            {inv.currency} {inv.amount.toFixed(2)}
                          </td>
                          <td>
                            <span className={`badge bg-${inv.status === 'paid' ? 'success' : inv.status === 'open' ? 'warning' : 'secondary'} bg-opacity-10 text-${inv.status === 'paid' ? 'success' : inv.status === 'open' ? 'warning' : 'secondary'}`} style={{ fontSize: '10px' }}>
                              {inv.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-muted2)', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inv.description || '—'}
                          </td>
                          <td>
                            {inv.hostedUrl ? (
                              <a href={inv.hostedUrl} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: '#6f42c1' }}>
                                View <i className="bi bi-box-arrow-up-right"></i>
                              </a>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Gift orders sent by this user */}
              {giftOrders && giftOrders.length > 0 && (
                <div className="mt-4">
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', marginBottom: '8px' }}>Gift Stories Sent</div>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>CHILD</th>
                          <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>STORY</th>
                          <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>STATUS</th>
                          <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>DATE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {giftOrders.map(g => (
                          <tr key={g._id}>
                            <td style={{ fontSize: '13px', color: 'var(--text-main)' }}>{g.childName}</td>
                            <td style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{g.storyTitle || '—'}</td>
                            <td>
                              <span className={`badge bg-${g.status === 'delivered' ? 'success' : g.status === 'failed' ? 'danger' : 'warning'} bg-opacity-10 text-${g.status === 'delivered' ? 'success' : g.status === 'failed' ? 'danger' : 'warning'}`} style={{ fontSize: '10px' }}>
                                {g.status}
                              </span>
                            </td>
                            <td style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{new Date(g.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent stories */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Recent Stories ({storyCount} total)</h6>
              {recentStories.length === 0 ? (
                <p className="text-muted small">No stories yet.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>TITLE</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>LANG</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>READS</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>ILLUS</th>
                        <th style={{ fontSize: '11px', color: 'var(--text-muted2)', fontWeight: 600 }}>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentStories.map(s => (
                        <tr key={s._id}>
                          <td style={{ fontSize: '13px', color: 'var(--text-main)' }}>
                            {s.isFavorite && <i className="bi bi-star-fill text-warning me-1" style={{ fontSize: '11px' }}></i>}
                            {s.title}
                          </td>
                          <td style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{s.language}</td>
                          <td style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{s.readCount}</td>
                          <td>
                            <span className={`badge bg-${s.illustrationStatus === 'done' ? 'success' : s.illustrationStatus === 'failed' ? 'danger' : 'secondary'} bg-opacity-10 text-${s.illustrationStatus === 'done' ? 'success' : s.illustrationStatus === 'failed' ? 'danger' : 'secondary'}`} style={{ fontSize: '10px' }}>
                              {s.illustrationStatus}
                            </span>
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUserDetail;
